import * as React from 'react';
import * as DialogPrimitive from '@radix-ui/react-dialog';
import { VisuallyHidden } from '@radix-ui/react-visually-hidden';
import { X } from 'lucide-react';

import { cn } from '@/utils';
import { ModalIllustration, ModalIllustrationPosition } from '@/components/ui/modal-illustrations';

const Dialog = DialogPrimitive.Root;

const DialogTrigger = DialogPrimitive.Trigger;

const DialogPortal = DialogPrimitive.Portal;

const DialogClose = DialogPrimitive.Close;

const DialogOverlay = React.forwardRef<
	React.ElementRef<typeof DialogPrimitive.Overlay>,
	React.ComponentPropsWithoutRef<typeof DialogPrimitive.Overlay>
>(({ className, ...props }, ref) => (
	<DialogPrimitive.Overlay
		ref={ref}
		className={cn(
			'data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 fixed inset-0 z-50 bg-black/50 backdrop-blur-sm',
			className,
		)}
		{...props}
	/>
));
DialogOverlay.displayName = DialogPrimitive.Overlay.displayName;

type PresenceRegistration = () => () => void;
const DialogTitlePresenceContext = React.createContext<PresenceRegistration | null>(null);
const DialogDescriptionPresenceContext = React.createContext<PresenceRegistration | null>(null);

const DialogContent = React.forwardRef<
	React.ElementRef<typeof DialogPrimitive.Content>,
	React.ComponentPropsWithoutRef<typeof DialogPrimitive.Content> & {
		isClosable?: boolean;
		illustrations?: ModalIllustration[];
		accessibilityTitle?: string;
		accessibilityDescription?: string;
	}
>(
	(
		{
			className,
			children,
			isClosable = true,
			illustrations,
			accessibilityTitle = 'Dialog window',
			accessibilityDescription = 'Dialog content',
			...props
		},
		ref,
	) => {
		const [titleCount, setTitleCount] = React.useState(0);
		const [descriptionCount, setDescriptionCount] = React.useState(0);

		const registerTitle = React.useCallback<PresenceRegistration>(() => {
			setTitleCount((count) => count + 1);
			return () => setTitleCount((count) => Math.max(0, count - 1));
		}, []);

		const registerDescription = React.useCallback<PresenceRegistration>(() => {
			setDescriptionCount((count) => count + 1);
			return () => setDescriptionCount((count) => Math.max(0, count - 1));
		}, []);

		return (
			<DialogPortal>
				<DialogOverlay />
				<DialogTitlePresenceContext.Provider value={registerTitle}>
					<DialogDescriptionPresenceContext.Provider value={registerDescription}>
						<DialogPrimitive.Content
							ref={ref}
							className={cn(
								' bg-card text-card-foreground data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95 data-[state=closed]:slide-out-to-top-[12%] data-[state=open]:slide-in-from-top-[12%] fixed top-1/2 left-1/2 z-60 grid w-full max-w-lg translate-x-[-50%] translate-y-[-50%] gap-4 rounded-xl border-2 border-border p-6 shadow-[0_4px_0_0_var(--border-shadow)] duration-200 max-h-[calc(100vh-2.5rem)]',
								className,
							)}
							{...props}
						>
							{titleCount === 0 ? (
								<VisuallyHidden>
									<DialogPrimitive.Title>{accessibilityTitle}</DialogPrimitive.Title>
								</VisuallyHidden>
							) : null}
							{descriptionCount === 0 ? (
								<VisuallyHidden>
									<DialogPrimitive.Description>{accessibilityDescription}</DialogPrimitive.Description>
								</VisuallyHidden>
							) : null}
							{illustrations && illustrations.length > 0 && (
								<div className="pointer-events-none absolute inset-0 z-61">
									{illustrations.map((illu, i) => {
										const pos: ModalIllustrationPosition = illu.position ?? 'side-right';
										const base = 'absolute select-none pointer-events-none drop-shadow-[0_6px_0_rgba(0,0,0,0.12)]';
										const posClass =
											pos === 'side-left'
												? 'left-0 bottom-4 -translate-x-1/2'
												: pos === 'side-right'
													? 'right-0 bottom-4 translate-x-1/2'
													: pos === 'top'
														? 'top-0 left-1/4 -translate-x-1/2 -translate-y-1/2'
														: 'bottom-0 left-1/2 -translate-x-1/2 translate-y-1/2';
										return <img key={i} src={illu.src} alt={illu.alt || ''} className={cn(base, posClass, illu.className)} />;
									})}
								</div>
							)}
							{children}
							{isClosable && (
								<DialogPrimitive.Close className="absolute top-3 right-3 z-70 ring-offset-background focus:ring-ring data-[state=open]:bg-accent data-[state=open]:text-muted-foreground rounded-sm opacity-80 transition-opacity hover:opacity-100 focus:ring-2 focus:ring-offset-2 focus:outline-none disabled:pointer-events-none">
									<X className="h-5 w-5" />
									<span className="sr-only">Close</span>
								</DialogPrimitive.Close>
							)}
						</DialogPrimitive.Content>
					</DialogDescriptionPresenceContext.Provider>
				</DialogTitlePresenceContext.Provider>
			</DialogPortal>
		);
	},
);
DialogContent.displayName = DialogPrimitive.Content.displayName;

const DialogHeader = ({ className, ...props }: React.HTMLAttributes<HTMLDivElement>) => (
	<div className={cn('relative z-62 flex flex-col space-y-1.5 text-center sm:text-left', className)} {...props} />
);
DialogHeader.displayName = 'DialogHeader';

const DialogFooter = ({ className, ...props }: React.HTMLAttributes<HTMLDivElement>) => (
	<div
		className={cn(
			'sticky bottom-0 translate-y-[3px] left-0 z-62 flex w-full flex-row flex-wrap justify-end gap-2 rounded-b-xl bg-inherit',
			className,
		)}
		{...props}
	/>
);
DialogFooter.displayName = 'DialogFooter';

const DialogTitle = React.forwardRef<React.ElementRef<typeof DialogPrimitive.Title>, React.ComponentPropsWithoutRef<typeof DialogPrimitive.Title>>(
	({ className, ...props }, ref) => {
		const register = React.useContext(DialogTitlePresenceContext);

		React.useEffect(() => {
			if (!register) return;
			return register();
		}, [register]);

		return <DialogPrimitive.Title ref={ref} className={cn('relative z-62 text-lg leading-none tracking-tight', className)} {...props} />;
	},
);
DialogTitle.displayName = DialogPrimitive.Title.displayName;

const DialogDescription = React.forwardRef<
	React.ElementRef<typeof DialogPrimitive.Description>,
	React.ComponentPropsWithoutRef<typeof DialogPrimitive.Description>
>(({ className, ...props }, ref) => {
	const register = React.useContext(DialogDescriptionPresenceContext);

	React.useEffect(() => {
		if (!register) return;
		return register();
	}, [register]);

	return <DialogPrimitive.Description ref={ref} className={cn('relative z-62 text-muted-foreground text-sm', className)} {...props} />;
});
DialogDescription.displayName = DialogPrimitive.Description.displayName;

export { Dialog, DialogPortal, DialogOverlay, DialogClose, DialogTrigger, DialogContent, DialogHeader, DialogFooter, DialogTitle, DialogDescription };
